import React, { useCallback, useMemo } from 'react';

import { isMobile } from 'react-device-detect';
import { type To, useNavigate } from 'react-router-dom';

import { ReactComponent as ArrowLeftIcon } from '@pkg/assets/icons/v2/arrow-left.svg';
import { ReactComponent as CloseIcon } from '@pkg/assets/icons/v2/close.svg';
import { LinkCircleButton, YatMagnetism } from '@pkg/components';

import './BackButton.scss';

interface BackButtonProps {
    to: To;
    icon: 'arrow-left' | 'close';
    ariaLabel: string;
    forceAbsolute?: boolean;
    onClick?: () => void;
    state?: unknown;
    align?: 'left' | 'right';
}

const BackButton = ({ to, icon, ariaLabel, state, onClick, align = 'left', forceAbsolute }: BackButtonProps) => {
    const navigate = useNavigate();
    const handleBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const IconComponent = useMemo(() => {
        if (icon === 'arrow-left') {
            return ArrowLeftIcon;
        } else {
            return CloseIcon;
        }
    }, [icon]);

    const renderButton = useMemo(() => {
        if (to === 'goBack') {
            return (
                <button
                    className={`yat-circle-button yat-circle-button--outline ${
                        !isMobile && 'yat-circle-button--large'
                    }`}
                    aria-label={ariaLabel}
                    onClick={handleBack}
                >
                    <IconComponent />
                </button>
            );
        } else {
            return (
                <LinkCircleButton to={to} ariaLabel={ariaLabel} size="large" onClick={onClick} state={state}>
                    <IconComponent />
                </LinkCircleButton>
            );
        }
    }, [to, ariaLabel, handleBack, IconComponent, onClick, state]);

    const classes = useMemo(() => {
        const base = ['yat-picker-back-button'];
        if (align === 'right') {
            base.push('align-right');
        }
        if (forceAbsolute) {
            base.push('force-absolute');
        }
        return base;
    }, [align, forceAbsolute]);

    return <YatMagnetism classes={classes.join(' ')}>{renderButton}</YatMagnetism>;
};

export default BackButton;
